import { Box, HStack, Icon, Stack, Tag, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FiAward } from "react-icons/fi";
import {
  PortalAwardModel,
  WeightType,
  type AwardCriterionDto,
  type PortalSourcingEventDetailDto,
} from "../../../../autogen/bff-api";
import { TipTap } from "../../../../common/input/TipTap/TipTap";
import { SectionCard } from "./SectionCard";

export const EventAwardCriteriaSection = ({ event }: { event: PortalSourcingEventDetailDto }) => {
  const { t } = useTranslation();
  if (!event.awardCriteria) return null;

  return (
    <SectionCard icon={FiAward} title={t("Award criteria")}>
      <AwardCriteriaContent event={event} />
    </SectionCard>
  );
};

export const AwardCriteriaContent = ({ event }: { event: PortalSourcingEventDetailDto }) => {
  const { t } = useTranslation();
  const award = event.awardCriteria;
  if (!award) return null;
  const criteria = award.criteria ?? [];

  return (
    <Stack spacing="4">
      <HStack
        bg="smPrimarySofter"
        rounded="md"
        px="4"
        py="3"
        spacing="3"
        align="center"
      >
        <Icon as={FiAward} boxSize="5" color="smPrimary" />
        <Stack spacing="0">
          <Text fontSize="xs" color="smPrimary" textTransform="uppercase" letterSpacing="wide" fontWeight="semibold">
            {t("Award model")}
          </Text>
          <Text fontSize="sm" fontWeight="semibold" color="smText">
            {t(award.model)}
          </Text>
        </Stack>
      </HStack>
      {award.model === PortalAwardModel.LowestPrice ? (
        <Text fontSize="sm" color="smMuted">
          {t("The contract is awarded to the qualifying tender with the lowest price.")}
        </Text>
      ) : (
        <CriteriaList criteria={criteria} />
      )}
    </Stack>
  );
};

const CriteriaList = ({ criteria }: { criteria: AwardCriterionDto[] }) => {
  const { t } = useTranslation();
  if (criteria.length === 0) {
    return (
      <Text fontSize="sm" color="smMuted">
        {t("No criteria have been published for this procurement yet.")}
      </Text>
    );
  }
  return (
    <Stack spacing="3">
      {criteria.map((criterion, index) => {
        const heading = criterion.name?.trim() || t(criterion.type);
        const weight = criterion.weight
          ? criterion.weight.type === WeightType.Percentage
            ? `${criterion.weight.number} %`
            : `${criterion.weight.number} ${t("points")}`
          : null;
        return (
          <Box
            key={`${criterion.type}-${index}`}
            borderWidth="1px"
            borderColor="smBorder"
            rounded="md"
            px="4"
            py="3"
            bg="smBackground"
          >
            <HStack justify="space-between" align="start" spacing="3">
              <Stack spacing="2" flex="1">
                <HStack spacing="2" align="center" flexWrap="wrap">
                  <Tag size="sm" colorScheme="gray" variant="subtle">
                    {t(criterion.type)}
                  </Tag>
                  <Text fontWeight="semibold" fontSize="sm">
                    {heading}
                  </Text>
                </HStack>
                {criterion.description && (
                  <TipTap content={criterion.description} editable={false} hasBorder={false} padding="0" maxHeight="" />
                )}
              </Stack>
              {weight && (
                <Box
                  bg="smPrimarySofter"
                  color="smPrimary"
                  px="2.5"
                  py="1"
                  rounded="md"
                  fontSize="xs"
                  fontWeight="semibold"
                  flexShrink="0"
                >
                  {weight}
                </Box>
              )}
            </HStack>
          </Box>
        );
      })}
    </Stack>
  );
};
